/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   โปรเจ็กต์ของลูกค้า   /portal/project/:code
   แท็บ: แบบบ้าน (เห็นทุกเวอร์ชัน) · RC · เอกสาร · ทีมงาน
============================================================ */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Avatar, Button, Card, Collapse, Descriptions, Empty, Flex, List, Progress, Spin, Tabs, Tag, Typography, message } from "antd";
import { LeftOutlined, MessageOutlined, PaperClipOutlined, PhoneOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import StatusTag from "../components/ak/StatusTag";
import FileList from "../components/ak/FileList";
import { RcStatusTag, RcTypeTag, costText } from "../components/ak/rc";
import { fileUrl, formatMoney } from "../utils/util";
import { openFile } from "../utils/fileOpen";
import { Portal, perr } from "./portalApi";
import PortalRcNew from "./PortalRcNew";

const d = (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "-");

export default function PortalProject() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [p, setP] = useState(null);
  const [newRc, setNewRc] = useState(false);
  const tab = sp.get("tab") || "plan";

  const load = () =>
    Portal.project(code)
      .then((r) => setP(r.data.data))
      .catch((e) => {
        message.error(perr(e));
        navigate("/portal", { replace: true });
      });

  useEffect(() => {
    load();
  }, [code]);

  const versions = useMemo(() => {
    const map = new Map();
    (p?.plans || []).forEach((f) => {
      if (!map.has(f.version_no)) map.set(f.version_no, { version_no: f.version_no, title: f.title, remark: f.remark, created_date: f.created_date, files: [] });
      map.get(f.version_no).files.push(f);
    });
    return [...map.values()];
  }, [p]);

  if (!p) return <Flex justify="center" style={{ padding: 60 }}><Spin /></Flex>;

  const open = (f, inline) => openFile(() => Portal.file("project", f.file_id, inline), f, inline).catch((e) => message.error(e.message));
  const site = [p.site_address, p.site_subdistrict, p.site_district, p.site_province, p.site_zipcode].filter(Boolean).join(" ");

  let pct = null;
  if (p.start_date && p.end_date) {
    const total = dayjs(p.end_date).diff(dayjs(p.start_date), "day") + 1;
    const passed = Math.min(total, Math.max(0, dayjs().startOf("day").diff(dayjs(p.start_date), "day") + 1));
    pct = Math.round((passed / total) * 100);
  }
  const rcOpen = p.rcs.filter((r) => !["เสร็จสิ้น", "ไม่อนุมัติ", "ยกเลิก"].includes(r.status)).length;

  const vHead = (v, latest) => (
    <Flex gap={8} align="center" wrap="wrap">
      <Typography.Text strong>เวอร์ชัน {v.version_no}</Typography.Text>
      {latest && <Tag color="green">ล่าสุด</Tag>}
      {v.title && <span>{v.title}</span>}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {dayjs(v.created_date).format("DD/MM/YYYY")}
      </Typography.Text>
    </Flex>
  );

  const planTab = versions.length ? (
    <>
      <div className="ak-plan-latest">
        {vHead(versions[0], true)}
        {versions[0].remark && <div className="pt-meta">{versions[0].remark}</div>}
        <FileList files={versions[0].files} onOpen={open} showDate={false} showTitle={false} />
      </div>
      {versions.length > 1 && (
        <Collapse
          ghost
          size="small"
          items={[
            {
              key: "old",
              label: `แบบเวอร์ชันก่อนหน้า (${versions.length - 1})`,
              children: versions.slice(1).map((v) => (
                <div key={v.version_no} className="ak-plan-old">
                  {vHead(v, false)}
                  <FileList files={v.files} onOpen={open} showDate={false} showTitle={false} />
                </div>
              )),
            },
          ]}
        />
      )}
    </>
  ) : (
    <Empty description="เจ้าหน้าที่ยังไม่ได้อัปโหลดแบบบ้าน" />
  );

  const rcTab = (
    <>
      <Flex justify="space-between" align="center" gap={8} wrap="wrap" className="mb-3">
        <Typography.Text type="secondary">ต้องการลด/เพิ่มงาน แก้ไขแบบ หรือแจ้งเรื่องอื่น ๆ</Typography.Text>
        {p.status !== "ส่งมอบแล้ว" && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setNewRc(true)}>
            แจ้งเรื่องใหม่
          </Button>
        )}
      </Flex>
      {p.rcs.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="ยังไม่มีเรื่องที่แจ้ง" />
      ) : (
        <List
          dataSource={p.rcs}
          renderItem={(r) => (
            <List.Item className="pt-rc-item" onClick={() => navigate(`/portal/rc/${r.rc_code}`)}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Flex justify="space-between" gap={8}>
                  <Typography.Text strong ellipsis>
                    {r.title}
                  </Typography.Text>
                  <RcStatusTag value={r.status} />
                </Flex>
                <div className="pt-meta">
                  <RcTypeTag value={r.rc_type} /> {r.rc_code} · {d(r.created_date)}
                  {costText(r.cost_change) ? ` · ${costText(r.cost_change)}` : ""}
                  {Number(r.file_count) > 0 && (
                    <>
                      {" · "}
                      <PaperClipOutlined /> {r.file_count}
                    </>
                  )}
                </div>
              </div>
            </List.Item>
          )}
        />
      )}
    </>
  );

  const teamTab = p.team.length ? (
    <List
      dataSource={p.team}
      renderItem={(t) => (
        <List.Item
          actions={[
            t.tel && (
              <Button key="t" shape="circle" icon={<PhoneOutlined />} href={`tel:${t.tel}`} aria-label="โทร" />
            ),
            t.line_id && (
              <Button key="l" shape="circle" icon={<MessageOutlined />} href={`https://line.me/ti/p/~${encodeURIComponent(t.line_id)}`} target="_blank" rel="noopener noreferrer" aria-label="LINE" />
            ),
          ].filter(Boolean)}
        >
          <List.Item.Meta
            avatar={<Avatar size={44} src={t.photo_path ? fileUrl(t.photo_path) : undefined} icon={<UserOutlined />} />}
            title={`${t.firstname || ""} ${t.lastname || ""}${t.nickname ? ` (${t.nickname})` : ""}`}
            description={
              <span>
                {t.team_role}
                {t.tel ? ` · ${t.tel}` : ""}
              </span>
            }
          />
        </List.Item>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="ยังไม่ได้กำหนดทีมงาน" />
  );

  return (
    <div>
      <Button type="link" icon={<LeftOutlined />} onClick={() => navigate("/portal")} className="pt-back">
        บ้านของฉัน
      </Button>
      <Card className="pt-card">
        <Flex gap={14} align="flex-start">
          {p.model_image && <img className="pt-hero-img" src={fileUrl(p.model_image)} alt="" />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography.Title level={4} className="!m-0">
              {p.project_name}
            </Typography.Title>
            <div className="pt-meta">
              {p.project_code}
              {p.model_code ? ` · แบบ ${p.model_code} ${p.model_name || ""}` : ""}
            </div>
            <div style={{ marginTop: 6 }}>
              <StatusTag value={p.status} />
              {p.selection && <Tag color={p.selection.status === "ล็อกสเปกแล้ว" ? "green" : "default"}>วัสดุ: {p.selection.status}</Tag>}
            </div>
          </div>
        </Flex>
        {pct !== null && (
          <div style={{ marginTop: 12 }}>
            <Flex justify="space-between" className="pt-meta">
              <span>เริ่ม {d(p.start_date)}</span>
              <span>กำหนดเสร็จ {d(p.end_date)}</span>
            </Flex>
            <Progress percent={pct} strokeColor="#9b6420" size="small" format={(v) => `${v}% ของเวลา`} />
          </div>
        )}
        <Descriptions size="small" column={1} className="pt-desc" style={{ marginTop: 8 }}>
          {site && <Descriptions.Item label="ที่ตั้ง">{site}</Descriptions.Item>}
          {p.usable_area && <Descriptions.Item label="พื้นที่ใช้สอย">{formatMoney(p.usable_area, 0)} ตร.ม.</Descriptions.Item>}
          {p.contract_no && <Descriptions.Item label="สัญญา">{`${p.contract_no} · ${d(p.contract_date)}`}</Descriptions.Item>}
        </Descriptions>
      </Card>

      <Card className="pt-card" styles={{ body: { paddingTop: 4 } }}>
        <Tabs
          activeKey={tab}
          onChange={(k) => setSp({ tab: k }, { replace: true })}
          items={[
            { key: "plan", label: `แบบบ้าน${versions.length ? ` (v${versions[0].version_no})` : ""}`, children: planTab },
            { key: "rc", label: <span>RC{rcOpen ? <Tag color="orange" style={{ marginLeft: 6, marginRight: 0 }}>{rcOpen}</Tag> : ""}</span>, children: rcTab },
            { key: "doc", label: `เอกสาร (${p.files.length})`, children: <FileList files={p.files} onOpen={open} emptyText="ยังไม่มีเอกสาร" /> },
            { key: "team", label: "ทีมงาน", children: teamTab },
          ]}
        />
      </Card>

      <PortalRcNew
        open={newRc}
        projectCode={code}
        onClose={() => setNewRc(false)}
        onDone={(rc) => navigate(`/portal/rc/${rc.rc_code}`)}
      />
    </div>
  );
}
